import type { MovementSource, MovementType, ProductType, VatTreatment } from '@zebra/core';

/**
 * Plain-English label (and, where it's genuinely non-obvious, a one-line
 * description) for enum codes that would otherwise leak to users verbatim
 * (guidance wave, 2026-08-25). Lifted out of `MovementLedgerReport.tsx` (its
 * local `MOVEMENT_TYPE_LABELS`) so the same translation is available wherever
 * a raw ledger movement type, or reading source, appears — e.g. `ClosePostPanel`,
 * `NewReadingFlow`, `OpenBatchModal` (not wired up yet; that's the next wave).
 * Read the real unions from `@zebra/core` (`enums.ts`) — never invent values.
 */

/** `movements.type` (`MovementType`, enums.ts). Kept as a plain string map so
 * existing consumers (Select `data`, Badge text) don't need to change shape. */
export const MOVEMENT_TYPE_LABELS: Record<MovementType, string> = {
  RECEIPT: 'Receipt',
  PRODUCTION_IN: 'Production in',
  PRODUCTION_OUT: 'Production out',
  ISSUE: 'Issue',
  DISPATCH: 'Dispatch',
  ADJUSTMENT: 'Adjustment',
  TRANSFER_IN: 'Transfer in',
  TRANSFER_OUT: 'Transfer out',
  PACK_IN: 'Pack in',
  PACK_OUT: 'Pack out',
  OPENING_BALANCE: 'Opening balance',
  RETURN_IN: 'Return (credit note)',
  RECOST: 'Recost',
};

/** One-line explanation of what triggers each movement type — the label alone
 * (e.g. "Recost") is still jargon to a first-time, Unleashed-trained user. */
export const MOVEMENT_TYPE_DESCRIPTIONS: Record<MovementType, string> = {
  RECEIPT: 'Goods received into stock from a supplier.',
  PRODUCTION_IN: 'Output added to stock when a production run or batch is closed and posted.',
  PRODUCTION_OUT: 'Input consumed from stock when a production run or batch is closed and posted.',
  ISSUE: 'Stock issued out against a cost centre — spares, consumables, or general usage.',
  DISPATCH: 'Stock shipped out to a customer.',
  ADJUSTMENT: 'A manual correction to a stock quantity, not tied to a physical movement.',
  TRANSFER_IN: 'Stock arriving at a location as one half of a transfer between locations.',
  TRANSFER_OUT: 'Stock leaving a location as one half of a transfer between locations.',
  PACK_IN: 'Packaged product added to stock by a pack conversion.',
  PACK_OUT: 'Bulk product consumed by a pack conversion.',
  OPENING_BALANCE: 'The starting stock quantity loaded when a product or tenant went live.',
  RETURN_IN: 'Stock returned to stock when a credit note restocks the goods.',
  RECOST: 'A weighted-average-cost correction posted when landed costs are allocated after receipt.',
};

/** `movements.source` / reading source (`MovementSource`, enums.ts) — shown raw
 * today in `NewReadingFlow`'s "Reading source" Select and `OpenBatchModal`. */
export const MOVEMENT_SOURCE_LABELS: Record<MovementSource, string> = {
  MEASURED: 'Measured',
  ESTIMATED: 'Estimated',
  DERIVED: 'Derived',
};

export const MOVEMENT_SOURCE_DESCRIPTIONS: Record<MovementSource, string> = {
  MEASURED: 'Read directly off a weighbridge, scale, or meter.',
  ESTIMATED: 'A visual or approximate reading, not taken from an instrument.',
  DERIVED: 'Calculated from other stock data rather than read or estimated directly.',
};

/** `products.type` (`ProductType`, enums.ts) — Products-module list/detail
 * (2026-08-26). Neither `ProductRegister.tsx` (Settings) nor the reports-hub
 * `ProductsRegister.tsx` had a label map to reuse (both render the raw enum
 * value in a Badge); added here, the established home for enum -> plain-English
 * translations, rather than duplicated a third time. */
export const PRODUCT_TYPE_LABELS: Record<ProductType, string> = {
  RAW_MATERIAL: 'Raw material',
  INTERMEDIATE: 'Intermediate',
  FINISHED_GOOD: 'Finished good',
  CONSUMABLE: 'Consumable',
  PACKAGING: 'Packaging',
};

/** `products.vatTreatment` (`VatTreatment`, enums.ts) — shown raw elsewhere
 * (e.g. InvoiceDetail.tsx's line table, settings/ProductRegister.tsx's Select
 * options); added here for the product detail page's header badge, the first
 * place this enum needed a plain-English label rather than the raw code. */
export const VAT_TREATMENT_LABELS: Record<VatTreatment, string> = {
  STANDARD: 'VAT standard (15%)',
  ZERO_RATED: 'VAT zero-rated',
  EXEMPT: 'VAT exempt',
};
